import React, { useState, useEffect } from "react";
import { SKILLMASTER_API } from "./config";
import { useNavigate } from "react-router-dom";
import "../styles/AdminConfig.css";

const AdminConfig = () => {
  const navigate = useNavigate();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveMessage, setSaveMessage] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);

  // Course visibility settings
  const [courseVisibility, setCourseVisibility] = useState({
    K12: true,
    Mathematics: true,
    Vocational: true,
    Industry: true,
    Technology: true,
    Agriculture: true,
    UPSC: true,
    Medical: true,
  });

  // Free books configuration
  const [freeBookSettings, setFreeBookSettings] = useState({
    K12: { enabled: false, allFree: false, freeBooks: [] },
    Mathematics: { enabled: false, allFree: false, freeBooks: [] },
    Vocational: { enabled: false, allFree: false, freeBooks: [] },
    Industry: { enabled: false, allFree: false, freeBooks: [] },
    Technology: { enabled: false, allFree: false, freeBooks: [] },
    Agriculture: { enabled: false, allFree: false, freeBooks: [] },
    UPSC: { enabled: false, allFree: false, freeBooks: [] },
    Medical: { enabled: false, allFree: false, freeBooks: [] },
  });

  // Available books per category
  const [availableBooks, setAvailableBooks] = useState({
    K12: [],
    Mathematics: [],
    Vocational: [],
    Industry: [],
    Technology: [],
    Agriculture: [],
    UPSC: [],
    Medical: [],
  });

  useEffect(() => {
    // Check if user is superadmin
    const userRole = localStorage.getItem("userRole");
    const interests = JSON.parse(localStorage.getItem("selectedInterests") || "[]");
    
    if (userRole === "superadmin" || interests.includes("All")) {
      setIsSuperAdmin(true);
      loadConfigFromServer(); // ✅ Load from server instead of localStorage
      loadBooksData();
    } else {
      navigate("/allpages");
    }
    
    setLoading(false);
  }, [navigate]);

  // ✅ NEW: Load configuration from server
  const loadConfigFromServer = async () => {
    try {
      const response = await fetch(`${SKILLMASTER_API}/admin/config`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && data.config) {
          console.log("✅ Configuration loaded from server:", data.config);
          
          if (data.config.courseVisibility) {
            setCourseVisibility(data.config.courseVisibility);
          }
          
          if (data.config.freeBookSettings) {
            setFreeBookSettings(data.config.freeBookSettings);
          }
        } else {
          console.log("ℹ️ No server configuration found, using defaults");
        }
      } else {
        console.log("⚠️ Could not load configuration from server, using defaults");
      }
    } catch (error) {
      console.error("❌ Error loading configuration from server:", error);
      console.log("ℹ️ Using default configuration");
    }
  };

  const loadBooksData = async () => {
    try {
      const response = await fetch(`${SKILLMASTER_API}/books`);
      if (response.ok) {
        const data = await response.json();
        
        const booksByCategory = {
          K12: [],
          Mathematics: [],
          Vocational: [],
          Industry: [],
          Technology: [],
          Agriculture: [],
          UPSC: [],
          Medical: [],
        };

        data.data?.forEach(book => {
          const category = book.source;
          if (category && booksByCategory[category]) {
            booksByCategory[category].push({
              id: book.pdf_name,
              name: book.pdf_name,
              price: book.price || "0",
            });
          }
        });

        setAvailableBooks(booksByCategory);
        console.log("✅ Books loaded by category:", booksByCategory);
      }
    } catch (error) {
      console.error("Error loading books:", error);
    }
  };

  const handleCourseVisibilityToggle = (course) => {
    setCourseVisibility(prev => ({
      ...prev,
      [course]: !prev[course]
    }));
  };

  const handleAllFreeToggle = (category) => {
    setFreeBookSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        allFree: !prev[category].allFree,
        freeBooks: !prev[category].allFree ? [] : prev[category].freeBooks
      }
    }));
  };

  const handleFreeBooksToggle = (category, bookId) => {
    setFreeBookSettings(prev => {
      const currentFreeBooks = prev[category].freeBooks || [];
      const isCurrentlyFree = currentFreeBooks.includes(bookId);
      
      return {
        ...prev,
        [category]: {
          ...prev[category],
          freeBooks: isCurrentlyFree
            ? currentFreeBooks.filter(id => id !== bookId)
            : [...currentFreeBooks, bookId]
        }
      };
    });
  };

  const handleCategoryFreeModeToggle = (category) => {
    setFreeBookSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        enabled: !prev[category].enabled,
        allFree: false,
        freeBooks: []
      }
    }));
  };

  // ✅ NEW: Save configuration to server
  const handleSaveConfiguration = async () => {
    setSaveLoading(true);
    setSaveMessage("");

    try {
      const configData = {
        courseVisibility,
        freeBookSettings
      };

      console.log("📤 Saving configuration to server:", configData);

      const response = await fetch(`${SKILLMASTER_API}/admin/config`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(configData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log("✅ Configuration saved to server successfully!");
        setSaveMessage("Configuration saved successfully! All users will see the changes.");
        
        // Optional: Also save to localStorage as backup
        localStorage.setItem("adminCourseVisibility", JSON.stringify(courseVisibility));
        localStorage.setItem("adminFreeBookSettings", JSON.stringify(freeBookSettings));
      } else {
        console.error("❌ Failed to save configuration:", data.message);
        setSaveMessage("Failed to save configuration. Please try again.");
      }
    } catch (error) {
      console.error("❌ Error saving configuration:", error);
      setSaveMessage("Network error. Could not save configuration.");
    } finally {
      setSaveLoading(false);
      setTimeout(() => setSaveMessage(""), 5000);
    }
  };

  const handleResetConfiguration = async () => {
    if (window.confirm("Are you sure you want to reset all configurations to default?")) {
      const defaultVisibility = {
        K12: true,
        Mathematics: true,
        Vocational: true,
        Industry: true,
        Technology: true,
        Agriculture: true,
        UPSC: true,
        Medical: true,
      };

      const defaultFreeSettings = {
        K12: { enabled: false, allFree: false, freeBooks: [] },
        Mathematics: { enabled: false, allFree: false, freeBooks: [] },
        Vocational: { enabled: false, allFree: false, freeBooks: [] },
        Industry: { enabled: false, allFree: false, freeBooks: [] },
        Technology: { enabled: false, allFree: false, freeBooks: [] },
        Agriculture: { enabled: false, allFree: false, freeBooks: [] },
        UPSC: { enabled: false, allFree: false, freeBooks: [] },
        Medical: { enabled: false, allFree: false, freeBooks: [] },
      };

      setCourseVisibility(defaultVisibility);
      setFreeBookSettings(defaultFreeSettings);

      // ✅ Save reset configuration to server
      try {
        const response = await fetch(`${SKILLMASTER_API}/admin/config`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            courseVisibility: defaultVisibility,
            freeBookSettings: defaultFreeSettings
          }),
        });

        if (response.ok) {
          console.log("✅ Configuration reset on server");
          setSaveMessage("Configuration reset to default and saved!");
        }
      } catch (error) {
        console.error("❌ Error resetting configuration:", error);
        setSaveMessage("Configuration reset locally, but could not sync to server.");
      }

      setTimeout(() => setSaveMessage(""), 3000);
    }
  };

  if (loading) {
    return <div className="admin-config-loading">Loading...</div>;
  }

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="admin-config-container">
      <div className="admin-config-header">
        <h1>Super Admin Configuration</h1>
        <p>Manage course visibility and free book settings for all users globally</p>
      </div>

      {saveMessage && (
        <div className={`save-message ${saveMessage.includes('Failed') || saveMessage.includes('error') ? 'error' : 'success'}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17L4 12" />
          </svg>
          {saveMessage}
        </div>
      )}

      <div className="admin-config-content">
        {/* Course Visibility Section */}
        <section className="config-section">
          <div className="section-header">
            <h2>Course Visibility for All Users</h2>
            <p>Control which courses are visible to users globally (affects all systems)</p>
          </div>
          
          <div className="visibility-grid">
            {Object.keys(courseVisibility).map((course) => (
              <div key={course} className="visibility-item">
                <div className="visibility-info">
                  <h3>{course}</h3>
                  <p>{courseVisibility[course] ? "Visible to all users" : "Hidden from all users"}</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={courseVisibility[course]}
                    onChange={() => handleCourseVisibilityToggle(course)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            ))}
          </div>
        </section>

        {/* Free Books Configuration Section */}
        <section className="config-section">
          <div className="section-header">
            <h2>Free Books Configuration</h2>
            <p>Set which books should be free in each category (applies globally)</p>
          </div>

          <div className="free-books-config">
            {Object.keys(freeBookSettings).map((category) => (
              <div key={category} className="category-config">
                <div className="category-header">
                  <h3>{category}</h3>
                  <label className="toggle-switch small">
                    <input
                      type="checkbox"
                      checked={freeBookSettings[category].enabled}
                      onChange={() => handleCategoryFreeModeToggle(category)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {freeBookSettings[category].enabled && (
                  <div className="category-config-content">
                    <div className="all-free-option">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={freeBookSettings[category].allFree}
                          onChange={() => handleAllFreeToggle(category)}
                        />
                        <span>Make all books in this category free</span>
                      </label>
                    </div>

                    {!freeBookSettings[category].allFree && (
                      <div className="books-list">
                        <h4>Select Individual Books:</h4>
                        {availableBooks[category].length > 0 ? (
                          <div className="books-grid">
                            {availableBooks[category].map((book) => (
                              <label key={book.id} className="book-checkbox">
                                <input
                                  type="checkbox"
                                  checked={freeBookSettings[category].freeBooks.includes(book.id)}
                                  onChange={() => handleFreeBooksToggle(category, book.id)}
                                />
                                <span className="book-name">{book.name}</span>
                                <span className="book-price">₹{book.price}</span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <p className="no-books">No books available in this category</p>
                        )}
                      </div>
                    )}

                    {freeBookSettings[category].allFree && (
                      <div className="all-free-indicator">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M20 6L9 17L4 12" />
                        </svg>
                        <span>All {availableBooks[category].length} books in this category are free</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Action Buttons */}
        <div className="config-actions">
          <button 
            className="btn-secondary" 
            onClick={handleResetConfiguration}
            disabled={saveLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M3 21v-5h5" />
            </svg>
            Reset to Default
          </button>
          <button 
            className="btn-primary" 
            onClick={handleSaveConfiguration}
            disabled={saveLoading}
          >
            {saveLoading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                Save Configuration Globally
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminConfig;